import { Container, Graphics, Sprite, Text as PixiText, Texture } from 'pixi.js';
import type { AsteroidData } from '../systems/NetworkManager';
import type { AsteroidSize, SelectionInfo, DamageResult } from '@astra/shared';
import { AsteroidGraphics, RESOURCE_COLORS, createTextureExplosion } from './AsteroidGraphics';
import { EventEmitter } from '../core/EventEmitter';
import { DamageableEntity } from './base/DamageableEntity';
import { assetLoader } from '../core/AssetLoader';

// Mining spark particle
interface MiningSpark {
  sprite: Sprite;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

// Shared particle texture for all asteroids
let sparkTextureGenerated = false;
let sparkTexture: Texture | null = null;

// Mass values for different asteroid sizes (relative to player mass of 100)
// Asteroids are now much larger - even small asteroids are massive obstacles
const ASTEROID_MASS: Record<AsteroidSize, number> = {
  small: 400,      // Large rock - will seriously damage small ships
  medium: 900,     // Massive obstacle - avoid at speed
  large: 2000,     // Enormous boulder - extremely dangerous
  huge: 4500,      // Gigantic behemoth - instant destruction on impact
};

// Degrees to radians
function degToRad(deg: number): number {
  return deg * (Math.PI / 180);
}

export class NetworkAsteroid extends DamageableEntity {
  // Required abstract properties from SpaceEntity
  readonly id: string;
  readonly entityType = 'asteroid' as const;

  // Required abstract properties from TargetableEntity
  readonly selectionRadius: number;
  readonly isHostile: boolean = false;

  // Required abstract properties from CollidableEntity
  readonly collisionRadius: number;
  readonly mass: number;

  // Backwards compatibility alias for id
  public get asteroidId(): string {
    return this.id;
  }

  // Asteroid-specific properties
  public size: AsteroidSize;
  public resourceType: string;
  public resourceAmount: number;

  // Angular velocity for rotation
  public angularVelocity: number = 0;

  private asteroidGraphics: AsteroidGraphics;
  private lastCollisionTime: number = 0;
  private collisionCooldown: number = 300; // 300ms between collision effects (unused, no damage)

  // Mining progress bar (drawn in this container while a miner is active; HP not shown)
  private asteroidHealthBarContainer: Container | null = null;
  private asteroidHealthBarGraphics: Graphics | null = null;
  private asteroidHealthBarVisible: boolean = false;
  private asteroidProgressLabel: PixiText | null = null;

  // Internal angle in degrees
  private _angle: number = 0;

  // Mining sparks effect - tracks miner positions for directional sparks
  private miningSparks: MiningSpark[] = [];
  private miningSparkTimer: number = 0;
  private minerPositions: Map<string, { x: number; y: number }> = new Map(); // minerId -> position
  private sparkParentContainer: Container | null = null;

  // Event emitter for local events
  public events: EventEmitter = new EventEmitter();

  public get angle(): number {
    return this._angle;
  }

  public set angle(value: number) {
    this._angle = value;
    this.rotation = degToRad(value);
  }

  constructor(data: AsteroidData) {
    super();

    this.position.set(data.x, data.y);

    this.id = data.id;
    this.size = data.size as AsteroidSize;
    this.resourceType = data.resourceType;
    this.resourceAmount = data.resourceAmount;
    this.mass = ASTEROID_MASS[this.size];

    // Create procedural asteroid graphics
    this.asteroidGraphics = new AsteroidGraphics(0, 0, this.size, this.resourceType);
    this.addChild(this.asteroidGraphics);

    // Set collision and selection radius based on graphics
    this.collisionRadius = this.asteroidGraphics.getRadius();
    this.selectionRadius = this.collisionRadius * 1.2; // Slightly larger for easier selection

    // Initialize hull from asteroid health (DamageableEntity uses hull/maxHull)
    this.hull = data.health;
    this.maxHull = data.maxHealth;
    this.shield = 0;
    this.maxShield = 0;

    // Random rotation
    this._angle = Math.random() * 360;
    this.rotation = degToRad(this._angle);
    this.angularVelocity = (Math.random() - 0.5) * 40; // degrees per second

    // Small random drift
    const driftSpeed = Math.random() * 15;
    const driftAngle = Math.random() * Math.PI * 2;
    this.velocityX = Math.cos(driftAngle) * driftSpeed;
    this.velocityY = Math.sin(driftAngle) * driftSpeed;

    // Create asteroid-specific health bar (doesn't rotate with asteroid)
    this.createAsteroidHealthBar();
  }

  // Implement abstract method from TargetableEntity
  getSelectionInfo(): SelectionInfo {
    return {
      type: 'asteroid',
      id: this.id,
      name: `${this.size.charAt(0).toUpperCase() + this.size.slice(1)} ${this.resourceType} Asteroid`,
      x: this.x,
      y: this.y,
      data: {
        size: this.size,
        resourceType: this.resourceType,
        resourceAmount: this.resourceAmount,
        health: this.hull,
        maxHealth: this.maxHull,
      },
    };
  }

  private createAsteroidHealthBar(): void {
    const radius = this.asteroidGraphics.getRadius();

    // Container for the mining-progress bar (positioned above asteroid, doesn't rotate with it)
    this.asteroidHealthBarContainer = new Container();
    this.asteroidHealthBarContainer.position.set(this.x, this.y - radius - 15);
    this.asteroidHealthBarContainer.zIndex = 100;
    this.asteroidHealthBarContainer.visible = false;

    this.asteroidHealthBarGraphics = new Graphics();
    this.asteroidHealthBarContainer.addChild(this.asteroidHealthBarGraphics);
  }

  // Get health bar container for parent to add to scene
  public getHealthBarContainer(): Container | null {
    return this.asteroidHealthBarContainer;
  }

  // HP-style bar is intentionally never shown; mining progress drives this slot instead.
  protected override showHealthBar(): void { /* no-op */ }

  /** Draw the mining progress bar (0..1 toward next whole unit) + "→ +1 RESOURCE" label. */
  public updateMiningProgress(progress: number, resourceType: string): void {
    if (!this.asteroidHealthBarContainer || !this.asteroidHealthBarGraphics) return;
    const clamped = Math.max(0, Math.min(1, progress));
    const barWidth = Math.max(40, this.asteroidGraphics.getRadius() * 1.5);
    const barHeight = 5;
    const x = -barWidth / 2;
    const y = 0;
    this.asteroidHealthBarGraphics.clear();
    // Backdrop
    this.asteroidHealthBarGraphics.rect(x - 1, y - 1, barWidth + 2, barHeight + 2);
    this.asteroidHealthBarGraphics.fill({ color: 0x101820, alpha: 0.85 });
    // Fill (resource-tinted)
    const fillColor = RESOURCE_COLORS[resourceType] || 0x44ffaa;
    this.asteroidHealthBarGraphics.rect(x, y, barWidth * clamped, barHeight);
    this.asteroidHealthBarGraphics.fill({ color: fillColor, alpha: 1 });
    // Border
    this.asteroidHealthBarGraphics.rect(x - 1, y - 1, barWidth + 2, barHeight + 2);
    this.asteroidHealthBarGraphics.stroke({ width: 1, color: 0x88aabb, alpha: 0.8 });

    // Label "→ +1 RESOURCE"
    const labelText = `→ +1 ${resourceType.split('_')[0].toUpperCase()}`;
    if (!this.asteroidProgressLabel) {
      this.asteroidProgressLabel = new PixiText({
        text: labelText,
        style: { fontFamily: 'monospace', fontSize: 11, fill: 0xcceeff, stroke: { color: 0x000000, width: 2 } },
      });
      this.asteroidProgressLabel.anchor.set(0.5, 1);
      this.asteroidProgressLabel.position.set(0, -2);
      this.asteroidHealthBarContainer.addChild(this.asteroidProgressLabel);
    } else if (this.asteroidProgressLabel.text !== labelText) {
      this.asteroidProgressLabel.text = labelText;
    }

    this.asteroidHealthBarVisible = true;
    this.asteroidHealthBarContainer.visible = true;
  }

  public hideMiningProgress(): void {
    if (this.asteroidHealthBarContainer) {
      this.asteroidHealthBarVisible = false;
      this.asteroidHealthBarContainer.visible = false;
    }
  }

  /**
   * Add a miner to this asteroid (starts showing sparks from their position)
   * @param minerId - Unique ID of the miner (player sessionId or bot ID)
   * @param x - World X position of the miner
   * @param y - World Y position of the miner
   */
  public addMiner(minerId: string, x: number, y: number): void {
    this.minerPositions.set(minerId, { x, y });
  }

  /**
   * Update a miner's position (call each frame while mining)
   */
  public updateMinerPosition(minerId: string, x: number, y: number): void {
    if (this.minerPositions.has(minerId)) {
      this.minerPositions.set(minerId, { x, y });
    }
  }

  /**
   * Remove a miner from this asteroid (stops their sparks)
   */
  public removeMiner(minerId: string): void {
    this.minerPositions.delete(minerId);
    if (this.minerPositions.size === 0) this.hideMiningProgress();
  }

  /**
   * Check if asteroid is being mined
   */
  public get isBeingMined(): boolean {
    return this.minerPositions.size > 0;
  }

  /**
   * Set the parent container for spark particles (world container)
   */
  public setSparkParent(parent: Container): void {
    this.sparkParentContainer = parent;
  }

  private ensureSparkTextureExists(): void {
    if (sparkTextureGenerated && sparkTexture) return;

    const gfx = new Graphics();
    gfx.star(4, 4, 4, 4, 2);
    gfx.fill({ color: 0xffffff, alpha: 1 });
    sparkTexture = assetLoader.generateTexture(gfx, 'asteroid-mining-spark');
    gfx.destroy();
    sparkTextureGenerated = true;
  }

  /**
   * Spawn mining sparks at the point facing each miner
   */
  private spawnMiningSparks(): void {
    if (!this.sparkParentContainer || this.minerPositions.size === 0) return;

    this.ensureSparkTextureExists();
    if (!sparkTexture) return;

    const resourceColor = RESOURCE_COLORS[this.resourceType] || 0x888888;
    const colors = [resourceColor, resourceColor, 0xff6600, 0xffaa00, 0xffffff];
    const radius = this.asteroidGraphics.getRadius();

    // Spawn sparks for each miner's mining point
    for (const [, minerPos] of this.minerPositions) {
      // Calculate angle from asteroid to miner
      const angleToMiner = Math.atan2(minerPos.y - this.y, minerPos.x - this.x);

      // Spawn point is on asteroid surface facing the miner
      const spawnX = this.x + Math.cos(angleToMiner) * radius;
      const spawnY = this.y + Math.sin(angleToMiner) * radius;

      // Spawn 1-2 sparks per miner
      const count = 1 + Math.floor(Math.random() * 2);
      for (let i = 0; i < count; i++) {
        const sprite = new Sprite(sparkTexture);
        sprite.anchor.set(0.5);
        sprite.position.set(spawnX, spawnY);
        sprite.tint = colors[Math.floor(Math.random() * colors.length)];
        sprite.scale.set(0.3 + Math.random() * 0.4);
        sprite.zIndex = 16;
        this.sparkParentContainer.addChild(sprite);

        // Sparks fly toward the miner with some spread
        const spreadAngle = angleToMiner + (Math.random() - 0.5) * Math.PI * 0.6;
        const speed = 50 + Math.random() * 70;

        this.miningSparks.push({
          sprite,
          vx: Math.cos(spreadAngle) * speed,
          vy: Math.sin(spreadAngle) * speed,
          life: 250 + Math.random() * 250,
          maxLife: 250 + Math.random() * 250,
        });
      }
    }
  }

  /**
   * Update mining sparks
   */
  private updateMiningSparks(delta: number): void {
    const dt = delta / 1000;

    for (let i = this.miningSparks.length - 1; i >= 0; i--) {
      const spark = this.miningSparks[i];

      // Move
      spark.sprite.x += spark.vx * dt;
      spark.sprite.y += spark.vy * dt;

      // Slow down
      spark.vx *= 0.97;
      spark.vy *= 0.97;

      // Fade out
      spark.life -= delta;
      const lifeRatio = Math.max(0, spark.life / spark.maxLife);
      spark.sprite.alpha = lifeRatio;

      // Scale down
      spark.sprite.scale.set(0.2 + 0.3 * lifeRatio);

      // Remove dead sparks
      if (spark.life <= 0) {
        spark.sprite.destroy();
        this.miningSparks.splice(i, 1);
      }
    }
  }

  // Implement update from SpaceEntity
  update(delta: number): void {
    if (this.isDestroyed) return;

    const dt = delta / 1000;

    // Apply angular velocity to container rotation
    this._angle += this.angularVelocity * dt;
    this.rotation = degToRad(this._angle);

    // Apply linear velocity with drag
    this.x += this.velocityX * dt;
    this.y += this.velocityY * dt;
    this.velocityX *= 0.995; // Very light drag
    this.velocityY *= 0.995;

    // Update health bar position (follows asteroid but doesn't rotate)
    if (this.asteroidHealthBarContainer) {
      const radius = this.asteroidGraphics.getRadius();
      this.asteroidHealthBarContainer.position.set(this.x, this.y - radius - 15);
    }

    // Mining sparks effect - spawn sparks when being mined
    if (this.minerPositions.size > 0 && this.sparkParentContainer) {
      this.miningSparkTimer += delta;
      if (this.miningSparkTimer >= 60) { // Every 60ms
        this.spawnMiningSparks();
        this.miningSparkTimer = 0;
      }
    }

    // Always update existing sparks (even if mining stopped, let them fade out)
    this.updateMiningSparks(delta);

    // Update stun state from base class
    this.updateStunState();
  }

  updateFromServer(data: Partial<AsteroidData>): void {
    if (data.health !== undefined) {
      this.hull = data.health;

      // Visual feedback when damaged
      if (this.hull < this.maxHull) {
        this.asteroidGraphics.alpha = 1.5;
        setTimeout(() => {
          if (this.asteroidGraphics) {
            this.asteroidGraphics.alpha = 1;
          }
        }, 50);
      }
    }

    if (data.resourceAmount !== undefined) {
      this.resourceAmount = data.resourceAmount;
    }
  }

  // Handle collision with player or other entity - NO DAMAGE
  // Per collisions.md: All collisions cause bounce + stun, but no damage
  // Asteroids can only be damaged by mining or weapons, not collisions
  public handleCollision(otherVelocity: { x: number; y: number }, otherMass: number): boolean {
    // No collision damage - asteroids are indestructible by ramming
    return false;
  }

  /**
   * Apply damage to asteroid (from collision or mining).
   * @param amount - Damage amount
   * @param fromMining - If true, yields resources when destroyed; if false (collision), no resources
   * @returns True if asteroid was destroyed
   */
  public applyDamage(amount: number, fromMining: boolean): boolean {
    // Use base class takeDamage for hull management
    const result = this.takeDamage(amount);

    // Visual feedback - flash
    this.asteroidGraphics.alpha = 1.5;
    setTimeout(() => {
      if (this.asteroidGraphics) {
        this.asteroidGraphics.alpha = 1;
      }
    }, 50);

    // If destroyed, emit event for game scene (onDestroy handles explosion)
    if (result.destroyed) {
      // Hide the graphics immediately so it doesn't show behind debris
      this.visible = false;
      // Emit event for game scene to handle
      this.events.emit('asteroidDestroyedLocally', {
        asteroidId: this.id,
        fromMining,
        resourceType: this.resourceType,
        resourceAmount: this.resourceAmount,
        x: this.x,
        y: this.y,
      });
    }

    return result.destroyed;
  }

  // Override onDestroy from DamageableEntity to create explosion effect
  protected override onDestroy(): void {
    this.explode();
  }

  // Create explosion when destroyed (public for external calls)
  public explode(): void {
    if (this.parent) {
      const color = RESOURCE_COLORS[this.resourceType] || 0x808080;
      // Switch to outline-only so fragments show only borders, not filled color
      this.asteroidGraphics.drawOutlineOnly();
      createTextureExplosion(
        this.parent,
        this.asteroidGraphics,
        this.x,
        this.y,
        this.rotation,
        color,
        this.asteroidGraphics.getRadius(),
      );
    }

    // Hide health bar
    if (this.asteroidHealthBarContainer) {
      this.asteroidHealthBarContainer.visible = false;
    }
  }

  // Get color based on resource type for UI hints
  getResourceColor(): number {
    return RESOURCE_COLORS[this.resourceType] || 0x4a5a6a;
  }

  // Get the asteroid's collision radius (alias for compatibility)
  getRadius(): number {
    return this.asteroidGraphics.getRadius();
  }

  // Cleanup
  override destroy(): void {
    if (this.asteroidHealthBarContainer) {
      this.asteroidHealthBarContainer.destroy({ children: true });
      this.asteroidHealthBarContainer = null;
    }
    this.asteroidHealthBarGraphics = null;
    this.asteroidProgressLabel = null;

    // Clean up mining sparks
    for (const spark of this.miningSparks) {
      spark.sprite.destroy();
    }
    this.miningSparks = [];

    // Call base class destroy (cleans up base health bar if any)
    super.destroy();
  }
}
